import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  api,
  Button,
  Card,
  Field,
  ConfirmDialog,
  PillButton,
  ActiveToggle,
  AvatarImage,
  ImageUploadField,
  validateDisplayName,
  validateLegalName,
  validatePrimaryDomain,
  validatePhone,
  validateDateOfBirth,
  validateAddress,
  isAddressBlank,
  formatUsPhone,
  normalizeStateCode,
  daysInMonth,
  US_STATES,
  MONTHS,
  apiErrorMessage,
  brandDisplayName,
  type AddressInput,
} from '@aralere/shared-ui';
import type {
  Client,
  ClientStatus,
  User,
  ListClientsResponse,
  Business,
  ClientUserSummary,
  SharedEmailMatch,
  UpdateClientRequest,
  PendingCreditsResponse,
  AddressLike,
  GlassIntensity,
  LateFeeConfig,
  EarlyPayDiscountOverride,
  TaxExemption,
  TaxExemptionReason,
  TaxProfileSummary,
} from '@aralere/shared-types';
import { ClientServicesPanel } from './ClientServicesPanel';
import { PendingCreditsDialog, refundDeepLink, resolvedSummary } from './PendingCreditsDialog';
import { TAX_EXEMPTION_REASON_OPTIONS, TAX_EXEMPTION_REASONS } from './taxCategory';

const STATUS_OPTIONS = [
  ['active', 'Active'],
  ['suspended', 'Suspended'],
  ['archived', 'Archived'],
] as const;

// Per-client glass intensity (spec §6.2). '' = inherit the brand default; the rest force a value.
const CLIENT_GLASS_OPTIONS = [
  ['', 'Inherit brand default'],
  ['default', 'Default'],
  ['frosted', 'Frosted (stronger blur)'],
  ['off', 'Off (flat)'],
] as const;

type EditableStatus = 'active' | 'suspended' | 'archived';

type FormState = {
  displayName: string;
  contactName: string;
  legalName: string;
  primaryDomain: string;
  phone: string;
  line1: string;
  line2: string;
  city: string;
  region: string;
  postalCode: string;
  country: string;
  dobMonth: string;
  dobDay: string;
  dobYear: string;
  status: EditableStatus | 'invited';
  glassIntensityOverride: string; // '' = inherit brand default; else 'default' | 'frosted' | 'off'
  // Time-tracking billing overrides (Stage-3 slice 3; Q40/Q44); '' = inherit the brand default.
  timeIncrementOverride: string; // '' | '15' | '30'
  hourlyRateOverride: string;
  notionalRateOverride: string;
  mileageRateOverride: string; // '' = inherit the brand's mileage rate resolution
  // Non-payment lifecycle overrides (Stage-4 slice 8b). Late fee: '' = inherit; 'custom' = the
  // config below (value 0/blank = exempt this client). Grace: '' = inherit the brand default.
  lateFeeOverrideMode: string; // '' | 'custom'
  lateFeeType: string;         // 'flat' | 'percent'
  lateFeeValue: string;        // percent as "1.5" (rate 0.015), flat as "25.00"; blank/0 = exempt
  lateFeeGraceDays: string;
  suspendGraceDaysOverride: string; // '' = inherit brand default
  // Early-pay discount override (04 #69): '' = inherit the brand default; 'custom' = the terms
  // below (percent 0/blank = no discount for this client, the late-fee exempt convention).
  earlyPayOverrideMode: string; // '' | 'custom'
  earlyPayRatePct: string;      // "2" = rate 0.02
  earlyPayDays: string;         // brand-local calendar days from the invoice date, 0-365
  // Sales tax (04 #160 WS-D; 02i T3.4 / T3.5 / §8 DP-10). The switch: '' = inherit the brand default,
  // 'on' / 'off' = taxEnabledOverride true / false. The rate: '' = the address decides, 'custom' = the percent
  // below (taxRateOverride). The exemption: '' = none, 'exempt' = the four fields below (taxExemption).
  taxEnabledMode: string;       // '' | 'on' | 'off'
  taxRateMode: string;          // '' | 'custom'
  taxRatePct: string;           // "8.75" = rate 0.0875
  taxExemptMode: string;        // '' | 'exempt'
  taxExemptReason: string;      // a TaxExemptionReason
  taxExemptCertificate: string; // the certificate REFERENCE (required while exempt)
  taxExemptFrom: string;        // YYYY-MM-DD
  taxExemptExpires: string;     // YYYY-MM-DD or '' = no expiry
  // The DELIVERY (ship-to) address (04 #160 WS-D; 02i T3.6 / §8 DP-11): the first `type: 'shipping'`
  // entry after the primary. Leave fully blank for none.
  shipLine1: string;
  shipLine2: string;
  shipCity: string;
  shipRegion: string;
  shipPostalCode: string;
  shipCountry: string;
};

/** The delivery-address slot: the first 'shipping' entry AFTER the primary (addresses[0]), as the server reads it. */
function shippingEntry(client: Client): AddressLike | undefined {
  return client.addresses.find((a, i) => i > 0 && a.type === 'shipping');
}

const YMD_RE = /^\d{4}-\d{2}-\d{2}$/;
const isYmd = (v: string): boolean => YMD_RE.test(v);

/** 08 Q71 (owner ruling s241, option (a)): true when a US state / region names NO loaded tax profile, so every
 *  invoice line to the client resolves 'not-registered' (nothing collected, the use-tax footnote; 02i §8 item
 *  10 Q2 (A)). Unknown (no profiles loaded yet) = no warning. Blank = no warning (the backup jurisdiction). */
function regionUncovered(region: string, profiles: TaxProfileSummary[] | null): boolean {
  const r = region.trim();
  if (!r || !profiles || profiles.length === 0) return false;
  const n = r.toLowerCase();
  return !profiles.some((p) => (p.stateCode ?? '').toLowerCase() === n || p.stateName.toLowerCase() === n);
}

type FieldErrors = Partial<Record<keyof FormState | 'dob', string>>;

function formFromClient(client: Client, primaryUser: User | null): FormState {
  const a = client.addresses[0];
  const ship = shippingEntry(client);
  const ex = client.taxExemption ?? null;
  const dob = (primaryUser?.dateOfBirth ?? '').split('-');
  const hasDob = dob.length === 3;
  return {
    displayName: client.displayName,
    contactName: primaryUser?.displayName ?? '',
    legalName: client.legalName,
    primaryDomain: client.primaryDomain ?? '',
    phone: formatUsPhone(client.phone ?? ''),
    line1: a?.line1 ?? '',
    line2: a?.line2 ?? '',
    city: a?.city ?? '',
    region: normalizeStateCode(a?.region ?? ''),
    postalCode: a?.postalCode ?? '',
    country: a?.country ?? '',
    dobMonth: hasDob ? String(Number(dob[1])) : '',
    dobDay: hasDob ? String(Number(dob[2])) : '',
    dobYear: hasDob ? dob[0] : '',
    status: client.status === 'invited' ? 'invited' : client.status,
    glassIntensityOverride: client.glassIntensityOverride ?? '',
    timeIncrementOverride: client.timeBillingIncrementOverride != null ? String(client.timeBillingIncrementOverride) : '',
    hourlyRateOverride: client.hourlyRateOverride != null ? String(client.hourlyRateOverride) : '',
    notionalRateOverride: client.notionalLaborRateOverride != null ? String(client.notionalLaborRateOverride) : '',
    mileageRateOverride: client.mileageRateOverride != null ? String(client.mileageRateOverride) : '',
    // Non-payment lifecycle overrides (slice 8b).
    lateFeeOverrideMode: client.lateFeeOverride ? 'custom' : '',
    lateFeeType: client.lateFeeOverride?.type ?? 'percent',
    lateFeeValue: client.lateFeeOverride
      ? client.lateFeeOverride.value > 0
        ? client.lateFeeOverride.type === 'percent'
          ? String(Number((client.lateFeeOverride.value * 100).toPrecision(12)))
          : String(client.lateFeeOverride.value)
        : ''
      : '',
    lateFeeGraceDays: client.lateFeeOverride ? String(client.lateFeeOverride.graceDays) : '',
    suspendGraceDaysOverride: client.suspendGraceDaysOverride != null ? String(client.suspendGraceDaysOverride) : '',
    // Early-pay discount override (04 #69).
    earlyPayOverrideMode: client.earlyPayDiscountOverride ? 'custom' : '',
    earlyPayRatePct: client.earlyPayDiscountOverride && client.earlyPayDiscountOverride.rate > 0
      ? String(Number((client.earlyPayDiscountOverride.rate * 100).toPrecision(12)))
      : '',
    earlyPayDays: client.earlyPayDiscountOverride ? String(client.earlyPayDiscountOverride.days) : '',
    // Sales tax (04 #160 WS-D).
    taxEnabledMode: client.taxEnabledOverride == null ? '' : client.taxEnabledOverride ? 'on' : 'off',
    taxRateMode: client.taxRateOverride != null ? 'custom' : '',
    taxRatePct: client.taxRateOverride != null ? String(Number((client.taxRateOverride * 100).toPrecision(12))) : '',
    taxExemptMode: ex ? 'exempt' : '',
    taxExemptReason: ex?.reason ?? 'resale',
    taxExemptCertificate: ex?.certificateRef ?? '',
    taxExemptFrom: ex?.effectiveFrom ?? '',
    taxExemptExpires: ex?.expiresAt ?? '',
    shipLine1: ship?.line1 ?? '',
    shipLine2: ship?.line2 ?? '',
    shipCity: ship?.city ?? '',
    shipRegion: normalizeStateCode(ship?.region ?? ''),
    shipPostalCode: ship?.postalCode ?? '',
    shipCountry: ship?.country ?? '',
  };
}

const pad2 = (s: string): string => s.padStart(2, '0');

/** Compose 'YYYY-MM-DD' when all three DOB parts are set, else ''. */
function composeDob(f: FormState): string {
  if (f.dobMonth && f.dobDay && f.dobYear) return `${f.dobYear}-${pad2(f.dobMonth)}-${pad2(f.dobDay)}`;
  return '';
}

/** True when some — but not all — DOB parts are filled in. */
function dobPartial(f: FormState): boolean {
  const filled = [f.dobMonth, f.dobDay, f.dobYear].filter(Boolean).length;
  return filled > 0 && filled < 3;
}

function addressInput(f: FormState): AddressInput {
  return {
    line1: f.line1, line2: f.line2, city: f.city,
    region: f.region, postalCode: f.postalCode, country: f.country,
  };
}

function shippingInput(f: FormState): AddressInput {
  return {
    line1: f.shipLine1, line2: f.shipLine2, city: f.shipCity,
    region: f.shipRegion, postalCode: f.shipPostalCode, country: f.shipCountry,
  };
}

/** The validateAddress error keys, re-homed onto the delivery block's form fields. */
const SHIP_KEY: Record<string, keyof FormState> = {
  line1: 'shipLine1', line2: 'shipLine2', city: 'shipCity', region: 'shipRegion', postalCode: 'shipPostalCode', country: 'shipCountry',
};

/** 04 #169 (a): an address block whose ONLY content is the State select. The block is then "not fully
 *  blank", so validateAddress asks for the street, city and postal code, while the State field, being
 *  filled, shows nothing and nothing names it as what keeps the block from counting as blank (the s241
 *  V8 finding). True when the region is set and every typed field is empty; country alone is a typed
 *  field and names itself through its own "required" sibling messages. */
const onlyRegionSet = (a: AddressInput): boolean =>
  a.region.trim() !== '' && [a.line1, a.line2, a.city, a.postalCode, a.country].every((v) => v.trim() === '');

const ONLY_REGION_MESSAGE = 'The state is set, so this counts as an address. Choose "-" here to leave it blank, or fill in the fields above.';

/** Validate the whole form against the Graph-format rules. Returns {} when clean. */
function validateForm(f: FormState, clientType: Client['clientType']): FieldErrors {
  const errors: FieldErrors = {};
  const set = (k: keyof FormState, e: string | null): void => {
    if (e) errors[k] = e;
  };
  set('displayName', validateDisplayName(f.displayName));
  if (clientType === 'business') {
    set('contactName', validateDisplayName(f.contactName));
    set('legalName', validateLegalName(f.legalName));
    set('primaryDomain', validatePrimaryDomain(f.primaryDomain));
  }
  set('phone', validatePhone(f.phone));
  if (clientType === 'consumer') {
    if (dobPartial(f)) errors.dob = 'Select month, day, and year.';
    else {
      const e = validateDateOfBirth(composeDob(f));
      if (e) errors.dob = e;
    }
  }
  const addrErrors = validateAddress(addressInput(f));
  for (const [k, v] of Object.entries(addrErrors)) {
    errors[k as keyof FormState] = v;
  }
  // 04 #169 (a): a State-only primary block names the State field as what holds the block open.
  if (onlyRegionSet(addressInput(f))) errors.region = ONLY_REGION_MESSAGE;
  // The delivery address follows the SAME rules as the primary (the server re-runs them), unless fully blank.
  if (!isAddressBlank(shippingInput(f))) {
    for (const [k, v] of Object.entries(validateAddress(shippingInput(f)))) {
      const key = SHIP_KEY[k];
      if (key) errors[key] = v;
    }
    // 04 #169 (a): the same on the delivery block, the surface the finding was made on.
    if (onlyRegionSet(shippingInput(f))) errors.shipRegion = ONLY_REGION_MESSAGE;
  }
  // Sales tax (04 #160 WS-D) - the server's tax-config.ts rules, mirrored so a 400 is the exception.
  if (f.taxRateMode === 'custom') {
    const pct = parseFloat(f.taxRatePct);
    if (!f.taxRatePct.trim() || !Number.isFinite(pct) || pct < 0 || pct >= 100) {
      errors.taxRatePct = 'Enter the rate as a percent from 0 to below 100 (8.75 = 8.75%).';
    }
  }
  if (f.taxExemptMode === 'exempt') {
    if (!(TAX_EXEMPTION_REASONS as readonly string[]).includes(f.taxExemptReason)) errors.taxExemptReason = 'Pick a reason.';
    const cert = f.taxExemptCertificate.trim();
    if (!cert) errors.taxExemptCertificate = 'Required: an exemption without a certificate reference does not exempt.';
    else if (cert.length > 64) errors.taxExemptCertificate = 'At most 64 characters (a reference, not the certificate itself).';
    if (!isYmd(f.taxExemptFrom)) errors.taxExemptFrom = 'Enter the date the certificate took effect.';
    if (f.taxExemptExpires && !isYmd(f.taxExemptExpires)) errors.taxExemptExpires = 'Enter a date, or leave blank for no expiry.';
    else if (f.taxExemptExpires && isYmd(f.taxExemptFrom) && f.taxExemptExpires < f.taxExemptFrom) {
      errors.taxExemptExpires = 'The certificate cannot expire before it takes effect.';
    }
  }
  return errors;
}

/** Build the PATCH from CHANGED fields only (so an untouched status never triggers a cascade). */
function buildPatch(f: FormState, client: Client, primaryUser: User | null): UpdateClientRequest {
  const patch: UpdateClientRequest = {};
  const a = client.addresses[0];

  if (f.displayName.trim() !== client.displayName) patch.displayName = f.displayName.trim();
  if (client.clientType === 'business' && f.contactName.trim() !== (primaryUser?.displayName ?? '')) {
    patch.contactName = f.contactName.trim();
  }
  if (client.clientType === 'business' && f.legalName.trim() !== client.legalName) {
    patch.legalName = f.legalName.trim();
  }
  if (client.clientType === 'business' && (f.primaryDomain.trim().toLowerCase() || null) !== (client.primaryDomain ?? null)) {
    patch.primaryDomain = f.primaryDomain.trim().toLowerCase() || null;
  }
  if ((f.phone.trim() || null) !== (client.phone ?? null)) patch.phone = f.phone.trim() || null;

  const blank = isAddressBlank(addressInput(f));
  const next: AddressLike | null = blank
    ? null
    : {
        type: a?.type ?? 'main',
        line1: f.line1.trim(),
        line2: f.line2.trim() || null,
        city: f.city.trim(),
        region: f.region.trim(),
        postalCode: f.postalCode.trim(),
        country: f.country.trim(),
      };
  const prev: AddressLike | null = a ?? null;
  if (JSON.stringify(next) !== JSON.stringify(prev)) patch.address = next;

  if (f.status !== 'invited' && f.status !== client.status) patch.status = f.status;

  if (client.clientType === 'consumer') {
    const oldDob = primaryUser?.dateOfBirth ?? null;
    const newDob = composeDob(f) || null;
    if (newDob !== oldDob) patch.dateOfBirth = newDob;
  }

  const curGlass = client.glassIntensityOverride ?? null;
  const nextGlass = f.glassIntensityOverride === '' ? null : (f.glassIntensityOverride as GlassIntensity);
  if (nextGlass !== curGlass) patch.glassIntensityOverride = nextGlass;

  // Time-tracking billing overrides (slice 3): blank = inherit (null). Server validates amounts.
  const nextInc = f.timeIncrementOverride === '15' ? 15 : f.timeIncrementOverride === '30' ? 30 : null;
  if (nextInc !== (client.timeBillingIncrementOverride ?? null)) patch.timeBillingIncrementOverride = nextInc;
  const money = (s: string): number | null => {
    const n = parseFloat(s);
    return s.trim() && Number.isFinite(n) && n >= 0 ? Math.round(n * 100) / 100 : null;
  };
  const nextRate = money(f.hourlyRateOverride);
  if (nextRate !== (client.hourlyRateOverride ?? null)) patch.hourlyRateOverride = nextRate;
  const nextNotional = money(f.notionalRateOverride);
  if (nextNotional !== (client.notionalLaborRateOverride ?? null)) patch.notionalLaborRateOverride = nextNotional;
  // Mileage rate override (Q43): a per-mile rate keeps up to 3 decimals (tenths of a cent).
  const perMile = (s: string): number | null => {
    const n = parseFloat(s);
    return s.trim() && Number.isFinite(n) && n >= 0 ? Math.round(n * 1000) / 1000 : null;
  };
  const nextMileage = perMile(f.mileageRateOverride);
  if (nextMileage !== (client.mileageRateOverride ?? null)) patch.mileageRateOverride = nextMileage;

  // Late-fee override (slice 8b): '' mode = inherit (null); 'custom' = a config (blank/0 = exempt).
  const nextLateFee: LateFeeConfig | null = (() => {
    if (f.lateFeeOverrideMode !== 'custom') return null;
    const type: 'flat' | 'percent' = f.lateFeeType === 'flat' ? 'flat' : 'percent';
    const raw = parseFloat(f.lateFeeValue);
    const value =
      f.lateFeeValue.trim() && Number.isFinite(raw) && raw > 0
        ? type === 'percent'
          ? Math.round((raw / 100) * 1e6) / 1e6
          : Math.round(raw * 100) / 100
        : 0;
    const g = parseInt(f.lateFeeGraceDays, 10);
    const graceDays = Number.isFinite(g) && g >= 0 && g <= 365 ? g : 0;
    return { type, value, graceDays };
  })();
  if (JSON.stringify(nextLateFee) !== JSON.stringify(client.lateFeeOverride ?? null)) {
    patch.lateFeeOverride = nextLateFee;
  }
  const graceI = parseInt(f.suspendGraceDaysOverride, 10);
  const nextGrace =
    f.suspendGraceDaysOverride.trim() && Number.isFinite(graceI) && graceI >= 0 && graceI <= 365 ? graceI : null;
  if (nextGrace !== (client.suspendGraceDaysOverride ?? null)) patch.suspendGraceDaysOverride = nextGrace;

  // Early-pay discount override (04 #69): '' mode = inherit (null); 'custom' = { rate, days }, rate 0
  // (blank) = no discount for this client. allowPartial is brand-level only and never sent here.
  const nextEarlyPay: EarlyPayDiscountOverride | null = (() => {
    if (f.earlyPayOverrideMode !== 'custom') return null;
    const raw = parseFloat(f.earlyPayRatePct);
    const rate = f.earlyPayRatePct.trim() && Number.isFinite(raw) && raw > 0 ? Math.round((raw / 100) * 1e6) / 1e6 : 0;
    const d = parseInt(f.earlyPayDays, 10);
    const days = Number.isFinite(d) && d >= 0 && d <= 365 ? d : 0;
    return { rate, days };
  })();
  if (JSON.stringify(nextEarlyPay) !== JSON.stringify(client.earlyPayDiscountOverride ?? null)) {
    patch.earlyPayDiscountOverride = nextEarlyPay;
  }

  // Sales tax (04 #160 WS-D): the switch, the rate (percent -> fraction, 6 decimals) and the exemption, each
  // sent only when it changed; the server's tax-config.ts validators are the authority (validateForm mirrors them).
  const nextTaxEnabled: boolean | null = f.taxEnabledMode === 'on' ? true : f.taxEnabledMode === 'off' ? false : null;
  if (nextTaxEnabled !== (client.taxEnabledOverride ?? null)) patch.taxEnabledOverride = nextTaxEnabled;
  const nextTaxRate: number | null = (() => {
    if (f.taxRateMode !== 'custom') return null;
    const pct = parseFloat(f.taxRatePct);
    return f.taxRatePct.trim() && Number.isFinite(pct) && pct >= 0 && pct < 100 ? Math.round((pct / 100) * 1e6) / 1e6 : null;
  })();
  if (nextTaxRate !== (client.taxRateOverride ?? null)) patch.taxRateOverride = nextTaxRate;
  const nextExemption: TaxExemption | null =
    f.taxExemptMode === 'exempt'
      ? {
          reason: f.taxExemptReason as TaxExemptionReason,
          certificateRef: f.taxExemptCertificate.trim(),
          effectiveFrom: f.taxExemptFrom,
          expiresAt: f.taxExemptExpires.trim() || null,
        }
      : null;
  if (JSON.stringify(nextExemption) !== JSON.stringify(client.taxExemption ?? null)) patch.taxExemption = nextExemption;

  // The delivery address (04 #160 WS-D): the first 'shipping' entry after the primary; blank = none (null removes it).
  const shipBlank = isAddressBlank(shippingInput(f));
  const nextShip: AddressLike | null = shipBlank
    ? null
    : {
        type: 'shipping',
        line1: f.shipLine1.trim(),
        line2: f.shipLine2.trim() || null,
        city: f.shipCity.trim(),
        region: f.shipRegion.trim(),
        postalCode: f.shipPostalCode.trim(),
        country: f.shipCountry.trim(),
      };
  const prevShip: AddressLike | null = shippingEntry(client) ?? null;
  if (JSON.stringify(nextShip) !== JSON.stringify(prevShip)) patch.shippingAddress = nextShip;

  return patch;
}

// ── Type-column / associations helpers (04 #15) ─────────────────────────────────
function capitalize(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

type ClientRow = ListClientsResponse['clients'][number];

/** "First +N" compaction for a list of named things. */
function firstPlusMore(items: { name: string }[]): string {
  if (items.length === 0) return '\u2014';
  const extra = items.length - 1;
  return extra > 0 ? `${items[0].name} +${extra}` : items[0].name;
}

/** Distinct businesses the person is tied to, earliest association first. */
function personBrands(user: User | null, businesses: Business[]): { id: string; name: string }[] {
  if (!user) return [];
  const earliest = new Map<string, string>();
  for (const a of user.clientAssociations) {
    const prev = earliest.get(a.businessId);
    if (!prev || a.addedAt < prev) earliest.set(a.businessId, a.addedAt);
  }
  return [...earliest.entries()]
    .sort((x, y) => (x[1] < y[1] ? -1 : x[1] > y[1] ? 1 : 0))
    .map(([id]) => ({ id, name: businesses.find((b) => b.id === id)?.name ?? id }));
}

/** Type-column third line: for a CONSUMER linked to a business (s33 linkedBusinessClientId) show the
 *  linked business's name, resolved from the already-loaded rows; otherwise an em dash. In the
 *  linked-account model a person is never associated to a business client, so there is nothing else
 *  to surface here. */
function typeThirdLine(client: Client, rows: ClientRow[]): string {
  if (client.clientType === 'consumer' && client.linkedBusinessClientId) {
    const name = rows.find((r) => r.client.id === client.linkedBusinessClientId)?.client.displayName;
    if (name) return name;
  }
  return '\u2014';
}

export function AdminClients(): JSX.Element {
  const [data, setData] = useState<ListClientsResponse | null>(null);
  const [businesses, setBusinesses] = useState<Business[]>([]);
  // HOUSE-CLIENT surface (slice 2, s43 record §3): the internal-vs-external list filter (internal
  // rows always VISIBLE by default — badged, never hidden) + the self-assign entry point state.
  const [internalFilter, setInternalFilter] = useState<'all' | 'internal' | 'external'>('all');
  const [selfAssignBusy, setSelfAssignBusy] = useState(false);
  const [selfAssignPick, setSelfAssignPick] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [syncWarning, setSyncWarning] = useState<string[] | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const [editing, setEditing] = useState<{ client: Client; primaryUser: User | null; signInUsername: string | null } | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  // 04 #160 WS-D / 08 Q71: the tax profiles the platform carries (served on GET mgmt-business, the Settings
  // pattern), read best-effort when the editor opens so the address blocks can warn about a region no
  // profile covers. null = not loaded (no warning is shown rather than a false one).
  const [taxProfiles, setTaxProfiles] = useState<TaxProfileSummary[] | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const navigate = useNavigate();
  // 04 #160 WS-C M2b (02i §8 DP-13; owner ruling s249 Q3): the ARCHIVE trigger is PRE-CHECKED and blocking.
  // An archived client never reaches the composer, so a credit left carrying would be the silent lapse
  // L-C forbids: the editor reads the client's pending credits first and archives only after a choice.
  const [archivePending, setArchivePending] = useState<{ pending: PendingCreditsResponse; patch: UpdateClientRequest } | null>(null);
  const formRef = useRef<HTMLDivElement>(null);

  // Client-logo management in the editor (04 #3). imgBust busts the serve route's short cache on
  // the open editor + the list thumbnails after a change.
  const [logoBusy, setLogoBusy] = useState(false);
  const [logoError, setLogoError] = useState<string | null>(null);
  const [imgBust, setImgBust] = useState(0);
  // Admin-managed primary-contact photo in the editor (04 #13 / 08 Q12). Shares imgBust.
  const [photoBusy, setPhotoBusy] = useState(false);
  const [photoError, setPhotoError] = useState<string | null>(null);
  // Users-on-this-client panel (04 #15): list + remove. Add is via the Invite tab (preselect a client).
  const [clientUsers, setClientUsers] = useState<ClientUserSummary[] | null>(null);
  const [clientUsersLoading, setClientUsersLoading] = useState(false);
  const [clientUsersError, setClientUsersError] = useState<string | null>(null);
  const [removingUserId, setRemovingUserId] = useState<string | null>(null);
  const [removeTarget, setRemoveTarget] = useState<{ userId: string; displayName: string } | null>(null);
  // Per-user credential actions (04 #9): password / MFA reset + contact-email change. Members only.
  const [userAction, setUserAction] = useState<{ userId: string; kind: 'password' | 'mfa' | 'email' } | null>(null);
  const [emailDraft, setEmailDraft] = useState('');
  const [actionBusy, setActionBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionResult, setActionResult] = useState<{ userId: string; text: string } | null>(null);
  // Shared-email override for the contact-email change (08 Q20): when the new email already belongs
  // to other member(s), the operator must accept the shared-inbox risk before it applies.
  const [sharedEmailMatches, setSharedEmailMatches] = useState<SharedEmailMatch[] | null>(null);
  const [sharedEmailAck, setSharedEmailAck] = useState(false);

  const load = async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      setData(await api.admin.listClients());
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  useEffect(() => {
    api.admin
      .listBusinesses()
      .then((r) => setBusinesses(r.businesses))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (editing) formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [editing]);

  const openEdit = (client: Client, primaryUser: User | null, signInUsername: string | null): void => {
    setEditing({ client, primaryUser, signInUsername });
    setForm(formFromClient(client, primaryUser));
    setFieldErrors({});
    setFormError(null);
    setLogoError(null);
    setPhotoError(null);
    setSyncWarning(null);
    setTaxProfiles(null);
    api.admin
      .getBusiness(client.primaryBusinessId)
      .then((r) => setTaxProfiles(r.taxProfiles ?? []))
      .catch(() => setTaxProfiles(null));
  };

  // ── Self-assign services (slice 2, s43 record §3; the entry point lives HERE for now — owner,
  // s45): get-or-create the brand's house client, then open the normal client editor on it — the
  // Services panel inside IS the self-assign surface (same engine, notices, packages, credentials).
  const startSelfAssign = async (): Promise<void> => {
    if (businesses.length > 1) {
      setSelfAssignPick(true);
      return;
    }
    if (businesses.length === 1) await openInternalClient(businesses[0].id);
  };

  const openInternalClient = async (businessId: string): Promise<void> => {
    setSelfAssignBusy(true);
    setError(null);
    try {
      const res = await api.admin.ensureInternalClient(businessId);
      setSelfAssignPick(false);
      await load();
      // The freshly ensured row may not be in `data` yet (state updates async) — open from the
      // response; the primary contact is the operator's own admin user (no member lookup needed).
      openEdit(res.client, null, null);
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setSelfAssignBusy(false);
    }
  };

  const closeForm = (): void => {
    setEditing(null);
    setForm(null);
    setFieldErrors({});
    setFormError(null);
    setLogoError(null);
    setPhotoError(null);
  };

  // Client-logo mutations (04 #3): update the open editor's snapshot from the response, bump the
  // cache-buster, and reload the list so its thumbnail refreshes too.
  const runLogo = async (fn: () => Promise<{ logoBlobRef: string | null }>): Promise<void> => {
    if (!editing) return;
    setLogoError(null);
    setLogoBusy(true);
    try {
      const res = await fn();
      setEditing((cur) => (cur ? { ...cur, client: { ...cur.client, logoBlobRef: res.logoBlobRef } } : cur));
      setImgBust((n) => n + 1);
      await load();
    } catch (e) {
      setLogoError((e as Error).message);
    } finally {
      setLogoBusy(false);
    }
  };

  // Primary-contact photo mutations (04 #13): update the editor's primaryUser snapshot from the
  // response, bump the shared cache-buster, and reload the list so its row thumbnail refreshes too.
  const runUserPhoto = async (fn: () => Promise<{ photoBlobRef: string | null }>): Promise<void> => {
    if (!editing?.primaryUser) return;
    setPhotoError(null);
    setPhotoBusy(true);
    try {
      const res = await fn();
      setEditing((cur) =>
        cur && cur.primaryUser ? { ...cur, primaryUser: { ...cur.primaryUser, photoBlobRef: res.photoBlobRef } } : cur,
      );
      setImgBust((n) => n + 1);
      await load();
    } catch (e) {
      setPhotoError((e as Error).message);
    } finally {
      setPhotoBusy(false);
    }
  };

  const loadClientUsers = async (clientId: string): Promise<void> => {
    setClientUsersLoading(true);
    setClientUsersError(null);
    try {
      const res = await api.admin.listClientUsers(clientId);
      setClientUsers(res.users);
    } catch (e) {
      setClientUsersError((e as Error).message);
      setClientUsers(null);
    } finally {
      setClientUsersLoading(false);
    }
  };

  useEffect(() => {
    if (editing?.client.id) {
      void loadClientUsers(editing.client.id);
    } else {
      setClientUsers(null);
      setClientUsersError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing?.client.id]);

  // Detach a non-primary user from this client, then refresh the panel + the main list (userCount).
  const removeUser = async (userId: string): Promise<void> => {
    if (!editing) return;
    setClientUsersError(null);
    setRemovingUserId(userId);
    try {
      await api.admin.removeClientUser(editing.client.id, userId);
      await loadClientUsers(editing.client.id);
      await load();
    } catch (e) {
      setClientUsersError((e as Error).message);
    } finally {
      setRemovingUserId(null);
    }
  };

  // n39 fork F (owner ruling): removing a member gets the same danger-confirm dialog its
  // destructive siblings already have; the row pill stays neutral and the dialog's confirm
  // carries the tone.
  const doRemoveUser = async (): Promise<void> => {
    if (!removeTarget) return;
    await removeUser(removeTarget.userId);
    setRemoveTarget(null);
  };

  // Per-user credential actions (04 #9). Members only; the panel below each user row drives these.
  const openUserAction = (u: ClientUserSummary, kind: 'password' | 'mfa' | 'email'): void => {
    setUserAction({ userId: u.userId, kind });
    setActionError(null);
    setActionResult(null);
    setEmailDraft(kind === 'email' ? u.email : '');
    setSharedEmailMatches(null);
    setSharedEmailAck(false);
  };

  const closeUserAction = (): void => {
    setUserAction(null);
    setActionError(null);
    setSharedEmailMatches(null);
    setSharedEmailAck(false);
  };

  const runUserAction = async (): Promise<void> => {
    if (!editing || !userAction) return;
    const { userId, kind } = userAction;
    const clientId = editing.client.id;
    const email = emailDraft.trim();
    if (kind === 'email' && !email.includes('@')) {
      setActionError('Enter a valid email address.');
      return;
    }
    setActionError(null);
    setActionBusy(true);
    try {
      if (kind === 'password') {
        const res = await api.admin.resetMemberPassword(clientId, userId);
        setActionResult({ userId, text: `Temporary password emailed to ${res.emailedTo}.` });
      } else if (kind === 'mfa') {
        const res = await api.admin.resetMemberMfa(clientId, userId);
        setActionResult({
          userId,
          text:
            res.removedMethods > 0
              ? `Reset ${res.removedMethods} authentication method${res.removedMethods === 1 ? '' : 's'}; the user must re-register.`
              : 'No authentication methods were registered, so there is nothing to reset.',
        });
      } else {
        const res = await api.admin.changeMemberEmail(clientId, userId, email, sharedEmailAck || undefined);
        if (res.status === 'needs-shared-email-override') {
          // Show the shared-inbox warning + require the risk checkbox; keep the panel open.
          setSharedEmailMatches(res.sharedWith ?? []);
          return;
        }
        setActionResult({
          userId,
          text:
            res.entraSync === 'ok'
              ? `Contact email updated to ${res.email ?? email}.`
              : `Contact email updated to ${res.email ?? email} (saved in our records; the Microsoft mirror was skipped).`,
        });
        await loadClientUsers(clientId); // editor user panel: email / sign-in username display changed
        await load(); // main client list: contact-email column would otherwise stay stale until reload
      }
      setUserAction(null);
    } catch (e) {
      setActionError((e as Error).message);
    } finally {
      setActionBusy(false);
    }
  };

  // Live validation: every change re-validates the whole form (fields interact, e.g. address group).
  const update = (patch: Partial<FormState>): void => {
    if (!form || !editing) return;
    const next = { ...form, ...patch };
    setForm(next);
    setFieldErrors(validateForm(next, editing.client.clientType));
  };

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (!form || !editing) return;

    const errors = validateForm(form, editing.client.clientType);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      setFormError('Fix the highlighted fields before saving.');
      return;
    }

    const patch = buildPatch(form, editing.client, editing.primaryUser);
    if (Object.keys(patch).length === 0) {
      closeForm();
      return;
    }

    if (patch.status === 'suspended' || patch.status === 'archived') {
      const verb = patch.status === 'suspended' ? 'Suspend' : 'Archive';
      const okToProceed = window.confirm(
        `${verb} “${editing.client.displayName}”?\n\n` +
          "This disables the primary contact's portal sign-in (their account in our tenant " +
          'is disabled; for guests, only our local guest object). You can set them back to ' +
          'Active at any time to re-enable sign-in.',
      );
      if (!okToProceed) return;
    }

    if (patch.status === 'archived') {
      // The DP-13 pre-check: any credit still owed opens the lapse dialog; the archive runs from its
      // onResolved. A failed read blocks the archive (it cannot be verified) and says so.
      setSubmitting(true);
      setFormError(null);
      try {
        const pending = await api.admin.pendingCredits(editing.client.id);
        if (pending.credits.length > 0) {
          setArchivePending({ pending, patch });
          return;
        }
      } catch (err) {
        setFormError(`The client's pending credits could not be read, so the archive was not applied: ${apiErrorMessage(err)}`);
        return;
      } finally {
        setSubmitting(false);
      }
    }

    await applyClientPatch(patch);
  };

  const applyClientPatch = async (patch: UpdateClientRequest): Promise<void> => {
    if (!editing) return;
    setSubmitting(true);
    setFormError(null);
    setSyncWarning(null);
    try {
      const res = await api.admin.updateClient(editing.client.id, patch);
      if (!res.entraSync.ok) setSyncWarning(res.entraSync.errors);
      await load();
      closeForm();
    } catch (err) {
      setFormError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  // Inline active⇄suspended toggle on the Clients list — reuses the SAME PATCH /api/mgmt-client
  // status mutation as the editor (Entra accountEnabled cascade on the primary contact, reported
  // via entraSync). Confirm only when disabling (→ suspended); re-enabling (→ active) is immediate.
  // Archived/invited rows render the read-only pill instead, so this only runs for active⇄suspended.
  const toggleClientActive = async (client: Client): Promise<void> => {
    const next: EditableStatus = client.status === 'active' ? 'suspended' : 'active';
    if (next === 'suspended') {
      const okToProceed = window.confirm(
        `Suspend “${client.displayName}”?\n\n` +
          "This disables the primary contact's portal sign-in (their account in our tenant " +
          'is disabled; for guests, only our local guest object). You can set them back to ' +
          'Active at any time to re-enable sign-in.',
      );
      if (!okToProceed) return;
    }
    setTogglingId(client.id);
    setError(null);
    setSyncWarning(null);
    try {
      const res = await api.admin.updateClient(client.id, { status: next });
      if (!res.entraSync.ok) setSyncWarning(res.entraSync.errors);
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setTogglingId(null);
    }
  };

  const isBusiness = editing?.client.clientType === 'business';
  const isConsumer = editing?.client.clientType === 'consumer';
  // 04 #160 WS-D: name the brand's own setting on the Inherit option, so "inherit" is never a mystery.
  const editorBrand = editing ? businesses.find((b) => b.id === editing.client.primaryBusinessId) ?? null : null;
  const brandChargeTax = editorBrand ? (editorBrand.taxEnabledDefault ? 'on' : 'off') : null;
  const primaryUncovered = form ? regionUncovered(form.region, taxProfiles) : false;
  const shipUncovered = form ? regionUncovered(form.shipRegion, taxProfiles) : false;
  const isGuest = editing?.primaryUser?.tenancyModel === 'b2b-guest';
  const editorBrands = editing?.primaryUser ? personBrands(editing.primaryUser, businesses) : [];

  const syncNote = isBusiness
    ? isGuest
      ? 'Business profile (guest primary contact): the company name syncs to each member’s Microsoft “Company” field. The guest contact’s identity lives in their own organization, so their name/phone/address stay in our records. Status changes enable/disable our local guest object.'
      : 'Business profile: the primary contact’s name syncs to their Microsoft display name, and the company name syncs to each member’s Microsoft “Company” field. Phone, address, and legal name stay in our records. Status changes enable/disable the contact’s sign-in.'
    : isGuest
      ? 'Guest account: profile fields are stored in our records only (their identity lives in their own organization). Status changes enable/disable our local guest object.'
      : 'Consumer member: name, phone, and address sync to the contact’s Microsoft account. Status changes enable/disable their sign-in.';

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl tracking-tight">Clients</h1>
          <p className="text-sm text-[var(--color-muted)]">
            {data?.clients.length ?? 0} total
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={internalFilter}
            onChange={(e) => setInternalFilter(e.target.value as 'all' | 'internal' | 'external')}
            className="rounded-md border border-[rgb(var(--edge-rgb)/0.10)] bg-[var(--surface-solid)] px-2 py-2 text-sm"
            aria-label="Filter internal clients"
          >
            <option value="all">All clients</option>
            <option value="external">External only</option>
            <option value="internal">Internal only</option>
          </select>
          <Button type="button" variant="ghost" disabled={selfAssignBusy} onClick={() => void startSelfAssign()}>
            {selfAssignBusy ? 'Opening…' : 'Self-assign services'}
          </Button>
          <Link to="/admin/clients/invite">
            <Button>Invite client</Button>
          </Link>
        </div>
      </header>

      {selfAssignPick ? (
        <div className="rounded-md border border-[rgb(var(--edge-rgb)/0.10)] bg-[rgb(var(--edge-rgb)/0.02)] px-4 py-3 text-sm">
          <p className="font-medium">Self-assign under which brand?</p>
          <p className="mt-0.5 text-xs text-[var(--color-muted)]">
            Each brand gets one reserved internal client (“your brand internal”). Services you
            assign to it run through the exact same engine as any client's; they license your own
            account and count in seat totals; cost stays on the linked license expense, and it is
            never billed.
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            {businesses.map((b) => (
              <PillButton key={b.id} disabled={selfAssignBusy} onClick={() => void openInternalClient(b.id)}>
                {brandDisplayName(b.name)}
              </PillButton>
            ))}
            <PillButton disabled={selfAssignBusy} onClick={() => setSelfAssignPick(false)}>
              Cancel
            </PillButton>
          </div>
        </div>
      ) : null}

      {syncWarning ? (
        <div className="rounded-md bg-[var(--tint-warn-bg-soft)] border border-[var(--tint-warn-border)] px-4 py-3 text-sm text-[var(--tint-warn-text)]">
          <p className="font-medium">Saved, but the Microsoft (Entra) sync had a problem:</p>
          <ul className="mt-1 list-disc pl-5">
            {syncWarning.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
          <p className="mt-1">Your changes are saved in our records. Re-save to retry the sync.</p>
        </div>
      ) : null}

      {editing && form ? (
        <div ref={formRef}>
          <Card>
            <form onSubmit={submit} className="space-y-5">
              <div className="flex items-start justify-between">
                <h2 className="font-display text-xl">Edit client</h2>
                <div className="text-right">
                  <span className="text-xs text-[var(--color-muted)] font-mono">{editing.client.id}</span>
                  {editorBrands.length > 0 ? (
                    <p className="text-[11px] text-[var(--color-muted)] mt-0.5">{editorBrands.map((b) => b.name).join(' · ')}</p>
                  ) : null}
                </div>
              </div>
              <p className="text-xs text-[var(--color-muted)]">{syncNote}</p>

              {isBusiness ? (
                <div className="rounded-md border border-[rgb(var(--edge-rgb)/0.10)] bg-[rgb(var(--edge-rgb)/0.02)] p-4">
                  <ImageUploadField
                    label="Company logo"
                    hint="JPG, PNG, or WebP up to 5 MB."
                    shape="square"
                    name={editing.client.displayName}
                    hasImage={!!editing.client.logoBlobRef}
                    src={
                      editing.client.logoBlobRef
                        ? `/api/client-logo?clientId=${encodeURIComponent(editing.client.id)}&v=${imgBust}`
                        : null
                    }
                    busy={logoBusy}
                    error={logoError}
                    onSelect={(file) => void runLogo(() => api.admin.uploadClientLogo(editing.client.id, file))}
                    onRemove={() => void runLogo(() => api.admin.deleteClientLogo(editing.client.id))}
                  />
                </div>
              ) : null}

              <div className="grid gap-4 md:grid-cols-3">
                <div>
                  {editing.signInUsername ? (
                    <Field label="Sign-in username">
                      <ReadOnlyBox value={editing.signInUsername} />
                    </Field>
                  ) : null}
                  <div className={editing.signInUsername ? 'mt-3' : ''}>
                    <Field label="Contact email">
                      <ReadOnlyBox value={editing.primaryUser?.email ?? '-'} />
                    </Field>
                  </div>
                  <p className="text-xs text-[var(--color-muted)] mt-1.5">
                    Read-only. Email changes arrive with the account-management tools.
                  </p>
                </div>
                <div>
                  {editing.primaryUser ? (
                    <ImageUploadField
                      label="Primary contact photo"
                      hint="JPG, PNG, or WebP up to 5 MB. Members also receive it as their Microsoft profile photo."
                      shape="circle"
                      name={editing.primaryUser.displayName ?? editing.client.displayName}
                      hasImage={!!editing.primaryUser.photoBlobRef}
                      src={
                        editing.primaryUser.photoBlobRef
                          ? `/api/avatar?userId=${encodeURIComponent(editing.primaryUser.id)}&v=${imgBust}`
                          : null
                      }
                      busy={photoBusy}
                      error={photoError}
                      onSelect={(file) => void runUserPhoto(() => api.admin.uploadUserPhoto(editing.primaryUser!.id, file))}
                      onRemove={() => void runUserPhoto(() => api.admin.deleteUserPhoto(editing.primaryUser!.id))}
                    />
                  ) : (
                    <div>
                      <p className="text-sm font-medium mb-2">Primary contact photo</p>
                      <p className="text-xs text-[var(--color-muted)]">No primary contact on this client.</p>
                    </div>
                  )}
                </div>
              </div>

              {isBusiness ? (
                <Field label="Full name" required error={fieldErrors.contactName}>
                  <Input
                    value={form.contactName}
                    onChange={(v) => update({ contactName: v })}
                    error={!!fieldErrors.contactName}
                    required
                  />
                </Field>
              ) : null}

              <div className="grid gap-4 md:grid-cols-3">
                <Field label={isBusiness ? 'Company name' : 'Full name'} required error={fieldErrors.displayName}>
                  <Input
                    value={form.displayName}
                    onChange={(v) => update({ displayName: v })}
                    error={!!fieldErrors.displayName}
                    required
                  />
                </Field>
                {isBusiness ? (
                  <Field label="Legal business name" error={fieldErrors.legalName}>
                    <Input
                      value={form.legalName}
                      onChange={(v) => update({ legalName: v })}
                      error={!!fieldErrors.legalName}
                      placeholder={form.displayName || 'Blank = uses Company name'}
                    />
                  </Field>
                ) : null}
                {isBusiness ? (
                  <Field label="Primary domain" error={fieldErrors.primaryDomain}>
                    <Input
                      value={form.primaryDomain}
                      onChange={(v) => update({ primaryDomain: v })}
                      error={!!fieldErrors.primaryDomain}
                      placeholder="acmecorp.com"
                      mono
                    />
                  </Field>
                ) : null}
              </div>

              <div className="grid gap-4 md:grid-cols-3">
                <Field label="Phone" error={fieldErrors.phone}>
                  <Input
                    value={form.phone}
                    onChange={(v) => update({ phone: formatUsPhone(v) })}
                    error={!!fieldErrors.phone}
                    placeholder="(555) 201-3344"
                  />
                </Field>
                {isConsumer ? (
                  <Field label="Date of birth" error={fieldErrors.dob}>
                    <DobPicker
                      month={form.dobMonth}
                      day={form.dobDay}
                      year={form.dobYear}
                      error={!!fieldErrors.dob}
                      onChange={(p) => update(p)}
                    />
                  </Field>
                ) : null}
                <Field label="Status" required>
                  <Select
                    value={form.status}
                    onChange={(v) => update({ status: v as FormState['status'] })}
                    options={
                      form.status === 'invited'
                        ? [['invited', 'Invited'] as const, ...STATUS_OPTIONS]
                        : STATUS_OPTIONS
                    }
                  />
                </Field>
                <Field label="Glass intensity">
                  <Select
                    value={form.glassIntensityOverride}
                    onChange={(v) => update({ glassIntensityOverride: v })}
                    options={CLIENT_GLASS_OPTIONS}
                  />
                </Field>
              </div>

              <div className="rounded-md border border-[rgb(var(--edge-rgb)/0.10)] bg-[rgb(var(--edge-rgb)/0.02)] p-4 space-y-4">
                <div>
                  <p className="text-sm font-medium">Billing overrides</p>
                  <p className="text-xs text-[var(--color-muted)]">
                    Blank inherits the brand default from Settings. The notional rate is your internal
                    profitability lens; it is never shown to the client and never invoiced. The mileage
                    rate overrides this client's billed rate per mile.
                  </p>
                </div>
                <div className="grid gap-4 md:grid-cols-3">
                  <Field label="Billing increment">
                    <Select
                      value={form.timeIncrementOverride}
                      onChange={(v) => update({ timeIncrementOverride: v })}
                      options={[
                        ['', 'Inherit brand default'],
                        ['30', '30 minutes'],
                        ['15', '15 minutes'],
                      ]}
                    />
                  </Field>
                  <Field label="Hourly rate ($/hr)">
                    <Input
                      value={form.hourlyRateOverride}
                      onChange={(v) => update({ hourlyRateOverride: v })}
                      placeholder="Inherit brand default"
                    />
                  </Field>
                  <Field label="Notional labor rate ($/hr)">
                    <Input
                      value={form.notionalRateOverride}
                      onChange={(v) => update({ notionalRateOverride: v })}
                      placeholder="Inherit brand default"
                    />
                  </Field>
                  <Field label="Mileage rate ($/mi)">
                    <Input
                      value={form.mileageRateOverride}
                      onChange={(v) => update({ mileageRateOverride: v })}
                      placeholder="Inherit brand rate"
                    />
                  </Field>
                </div>
              </div>

              <div className="rounded-md border border-[rgb(var(--edge-rgb)/0.10)] bg-[rgb(var(--edge-rgb)/0.02)] p-4 space-y-4">
                <div>
                  <p className="text-sm font-medium">Late fee, early-pay discount and non-payment</p>
                  <p className="text-xs text-[var(--color-muted)]">
                    Overrides the brand's late fee, early-pay discount and suspension grace for this client.
                    Inherit leaves the Settings default. For a custom late fee or discount, set the amount or
                    percent to 0 to exempt this client. Invoices already issued keep their terms.
                  </p>
                </div>
                <div className="grid gap-4 md:grid-cols-3">
                  <Field label="Late fee">
                    <Select
                      value={form.lateFeeOverrideMode}
                      onChange={(v) => update({ lateFeeOverrideMode: v })}
                      options={[
                        ['', 'Inherit brand default'],
                        ['custom', 'Set for this client'],
                      ]}
                    />
                  </Field>
                  {form.lateFeeOverrideMode === 'custom' ? (
                    <>
                      <Field label="Fee type">
                        <Select
                          value={form.lateFeeType}
                          onChange={(v) => update({ lateFeeType: v })}
                          options={[
                            ['percent', 'Percent'],
                            ['flat', 'Flat amount'],
                          ]}
                        />
                      </Field>
                      <Field label={form.lateFeeType === 'flat' ? 'Amount ($)' : 'Percent (%)'}>
                        <Input
                          value={form.lateFeeValue}
                          onChange={(v) => update({ lateFeeValue: v })}
                          placeholder="0 = exempt"
                        />
                      </Field>
                      <Field label="Grace days before fee">
                        <Input
                          value={form.lateFeeGraceDays}
                          onChange={(v) => update({ lateFeeGraceDays: v })}
                          placeholder="0"
                        />
                      </Field>
                    </>
                  ) : null}
                  <Field label="Suspend after (days past due)">
                    <Input
                      value={form.suspendGraceDaysOverride}
                      onChange={(v) => update({ suspendGraceDaysOverride: v })}
                      placeholder="Inherit brand default"
                    />
                  </Field>
                </div>
                {/* 04 #69: the per-client early-pay terms (rate + days; the partial-payment rule is
                    brand-level). Frozen onto each invoice at issue. */}
                <div className="grid gap-4 md:grid-cols-3">
                  <Field label="Early-pay discount">
                    <Select
                      value={form.earlyPayOverrideMode}
                      onChange={(v) => update({ earlyPayOverrideMode: v })}
                      options={[
                        ['', 'Inherit brand default'],
                        ['custom', 'Set for this client'],
                      ]}
                    />
                  </Field>
                  {form.earlyPayOverrideMode === 'custom' ? (
                    <>
                      <Field label="Percent off">
                        <Input
                          value={form.earlyPayRatePct}
                          onChange={(v) => update({ earlyPayRatePct: v })}
                          placeholder="0 = no discount"
                        />
                      </Field>
                      <Field label="Days from the invoice date">
                        <Input
                          value={form.earlyPayDays}
                          onChange={(v) => update({ earlyPayDays: v })}
                          placeholder="0"
                        />
                      </Field>
                    </>
                  ) : null}
                </div>
              </div>

              {/* 04 #160 WS-D (02i T3.4 / T3.5 / §8 DP-10): the client's sales-tax panel. The switch and the
                  rate are the s63 chain's per-client steps (the registration gate still wins); the exemption
                  zeroes every line while in force and is frozen on each invoice. Each change is a
                  configHistory 'client' row. */}
              <div className="rounded-md border border-[rgb(var(--edge-rgb)/0.10)] bg-[rgb(var(--edge-rgb)/0.02)] p-4 space-y-4">
                <div>
                  <p className="text-sm font-medium">Sales tax</p>
                  <p className="text-xs text-[var(--color-muted)]">
                    Whether and at what rate this client's invoices collect tax, and any exemption certificate on
                    file. The brand still collects only in the states it is registered in (Settings, Sales tax); a
                    client in another state gets no tax and the use-tax footnote whatever is set here. Invoices
                    already issued keep the tax they were issued with.
                  </p>
                </div>
                <div className="grid gap-4 md:grid-cols-3">
                  <Field label="Charge tax">
                    <Select
                      value={form.taxEnabledMode}
                      onChange={(v) => update({ taxEnabledMode: v })}
                      options={[
                        ['', brandChargeTax ? `Inherit brand default (${brandChargeTax === 'on' ? 'on' : 'off'})` : 'Inherit brand default'],
                        ['on', 'On for this client'],
                        ['off', 'Off for this client'],
                      ]}
                    />
                  </Field>
                  <Field label="Tax rate">
                    <Select
                      value={form.taxRateMode}
                      onChange={(v) => update({ taxRateMode: v })}
                      options={[
                        ['', "From the client's address"],
                        ['custom', 'Set for this client'],
                      ]}
                    />
                  </Field>
                  {form.taxRateMode === 'custom' ? (
                    <Field label="Rate (%)" error={fieldErrors.taxRatePct}>
                      <Input
                        value={form.taxRatePct}
                        onChange={(v) => update({ taxRatePct: v })}
                        error={!!fieldErrors.taxRatePct}
                        placeholder="8.75"
                      />
                    </Field>
                  ) : null}
                </div>
                <div className="grid gap-4 md:grid-cols-3">
                  <Field label="Exemption">
                    <Select
                      value={form.taxExemptMode}
                      onChange={(v) => update({ taxExemptMode: v })}
                      options={[
                        ['', 'None (taxed by the rules)'],
                        ['exempt', 'Exempt: a certificate is on file'],
                      ]}
                    />
                  </Field>
                  {form.taxExemptMode === 'exempt' ? (
                    <>
                      <Field label="Reason" required error={fieldErrors.taxExemptReason}>
                        <Select
                          value={form.taxExemptReason}
                          onChange={(v) => update({ taxExemptReason: v })}
                          options={TAX_EXEMPTION_REASON_OPTIONS}
                        />
                      </Field>
                      <Field label="Certificate reference" required error={fieldErrors.taxExemptCertificate}>
                        <Input
                          value={form.taxExemptCertificate}
                          onChange={(v) => update({ taxExemptCertificate: v })}
                          error={!!fieldErrors.taxExemptCertificate}
                          placeholder="e.g. the permit or certificate number"
                        />
                      </Field>
                      <Field label="In effect from" required error={fieldErrors.taxExemptFrom}>
                        <Input
                          type="date"
                          value={form.taxExemptFrom}
                          onChange={(v) => update({ taxExemptFrom: v })}
                          error={!!fieldErrors.taxExemptFrom}
                        />
                      </Field>
                      <Field label="Expires (blank = no expiry)" error={fieldErrors.taxExemptExpires}>
                        <Input
                          type="date"
                          value={form.taxExemptExpires}
                          onChange={(v) => update({ taxExemptExpires: v })}
                          error={!!fieldErrors.taxExemptExpires}
                        />
                      </Field>
                    </>
                  ) : null}
                </div>
                {form.taxExemptMode === 'exempt' ? (
                  <p className="text-xs text-[var(--color-muted)]">
                    While the certificate is in effect, no line to this client is taxed and the invoice says why
                    (the reason and the reference). An expired certificate stops exempting on its own; the
                    December tax review lists certificates that are missing a reference or expiring.
                  </p>
                ) : null}
              </div>

              <div className="rounded-md border border-[rgb(var(--edge-rgb)/0.10)] bg-[rgb(var(--edge-rgb)/0.02)] p-4 space-y-4">
                <p className="text-sm font-medium">Primary address (leave fully blank for none)</p>
                <div className="grid gap-4 md:grid-cols-2">
                  <Field label="Address line 1" error={fieldErrors.line1}>
                    <Input value={form.line1} onChange={(v) => update({ line1: v })} error={!!fieldErrors.line1} placeholder="123 Main St" />
                  </Field>
                  <Field label="Address line 2" error={fieldErrors.line2}>
                    <Input value={form.line2} onChange={(v) => update({ line2: v })} error={!!fieldErrors.line2} placeholder="Suite 400" />
                  </Field>
                </div>
                <div className="grid gap-4 md:grid-cols-4">
                  <Field label="City" error={fieldErrors.city}>
                    <Input value={form.city} onChange={(v) => update({ city: v })} error={!!fieldErrors.city} />
                  </Field>
                  <Field label="State / region" error={fieldErrors.region}>
                    <Select
                      value={form.region}
                      onChange={(v) => update({ region: v })}
                      options={[['', '-'] as const, ...US_STATES]}
                    />
                  </Field>
                  <Field label="Postal code" error={fieldErrors.postalCode}>
                    <Input value={form.postalCode} onChange={(v) => update({ postalCode: v })} error={!!fieldErrors.postalCode} placeholder="90001" />
                  </Field>
                  <Field label="Country" error={fieldErrors.country}>
                    <Input value={form.country} onChange={(v) => update({ country: v })} error={!!fieldErrors.country} placeholder="US" />
                  </Field>
                </div>
                {primaryUncovered ? <UncoveredRegionNote region={form.region} /> : null}
              </div>

              {/* 04 #160 WS-D (02i T3.6 / §8 DP-11): the delivery address, the first 'shipping' entry after the
                  primary. Read by a taxability rule sourced to the ship-to address; otherwise the billing
                  (primary) address decides. Never synced to Microsoft. */}
              <div className="rounded-md border border-[rgb(var(--edge-rgb)/0.10)] bg-[rgb(var(--edge-rgb)/0.02)] p-4 space-y-4">
                <div>
                  <p className="text-sm font-medium">Delivery address (leave fully blank when it is the primary address)</p>
                  <p className="text-xs text-[var(--color-muted)]">
                    Where goods or services are delivered when that differs from the billing address. A state's
                    tax rule that is sourced to the delivery address reads it; everything else uses the primary
                    address. Stored in our records only.
                  </p>
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  <Field label="Address line 1" error={fieldErrors.shipLine1}>
                    <Input value={form.shipLine1} onChange={(v) => update({ shipLine1: v })} error={!!fieldErrors.shipLine1} placeholder="123 Main St" />
                  </Field>
                  <Field label="Address line 2" error={fieldErrors.shipLine2}>
                    <Input value={form.shipLine2} onChange={(v) => update({ shipLine2: v })} error={!!fieldErrors.shipLine2} placeholder="Suite 400" />
                  </Field>
                </div>
                <div className="grid gap-4 md:grid-cols-4">
                  <Field label="City" error={fieldErrors.shipCity}>
                    <Input value={form.shipCity} onChange={(v) => update({ shipCity: v })} error={!!fieldErrors.shipCity} />
                  </Field>
                  <Field label="State / region" error={fieldErrors.shipRegion}>
                    <Select
                      value={form.shipRegion}
                      onChange={(v) => update({ shipRegion: v })}
                      options={[['', '-'] as const, ...US_STATES]}
                    />
                  </Field>
                  <Field label="Postal code" error={fieldErrors.shipPostalCode}>
                    <Input value={form.shipPostalCode} onChange={(v) => update({ shipPostalCode: v })} error={!!fieldErrors.shipPostalCode} placeholder="90001" />
                  </Field>
                  <Field label="Country" error={fieldErrors.shipCountry}>
                    <Input value={form.shipCountry} onChange={(v) => update({ shipCountry: v })} error={!!fieldErrors.shipCountry} placeholder="US" />
                  </Field>
                </div>
                {shipUncovered ? <UncoveredRegionNote region={form.shipRegion} /> : null}
              </div>

              <div className="rounded-md border border-[rgb(var(--edge-rgb)/0.10)] bg-[rgb(var(--edge-rgb)/0.02)] p-4">
                <p className="text-sm font-medium mb-2">{isBusiness ? 'Users billed to this business' : 'User administration'}</p>
                {clientUsersError ? (
                  <p className="text-sm text-[var(--tint-red-text)]">{clientUsersError}</p>
                ) : null}
                {clientUsersLoading && !clientUsers ? (
                  <p className="text-sm text-[var(--color-muted)]">Loading…</p>
                ) : null}
                {clientUsers ? (
                  clientUsers.length === 0 ? (
                    <p className="text-sm text-[var(--color-muted)]">No users on this client.</p>
                  ) : (
                    <ul className="divide-y divide-[rgb(var(--edge-rgb)/0.05)]">
                      {clientUsers.map((u) => {
                        const isMember = u.tenancyModel === 'member';
                        // For a linked roster row (a business's linked person) show their BUSINESS designation
                        // (linkedBusinessRole: billing contact / user), not the primary-contact role they hold
                        // on their own consumer account. A plain consumer membership keeps its association role.
                        const roleLabel = (
                          u.linkedConsumerClientId ? (u.linkedBusinessRole ?? 'user') : u.roleInClient
                        ).replace(/-/g, ' ');
                        return (
                          <li key={u.userId} className="py-2">
                            <div className="flex items-center justify-between gap-3">
                              <div className="min-w-0">
                                <p className="text-sm font-medium truncate">{u.displayName}</p>
                                <p className="text-xs text-[var(--color-muted)] truncate">
                                  {u.signInUsername ?? u.email}
                                  <span className="mx-1.5">·</span>
                                  <span className="uppercase tracking-wide">{roleLabel}</span>
                                  {u.status !== 'active' ? <span> · {u.status}</span> : null}
                                </p>
                              </div>
                              <div className="shrink-0 flex flex-wrap items-center justify-end gap-1.5">
                                {isMember ? (
                                  <>
                                    <PillButton onClick={() => openUserAction(u, 'password')}>
                                      Reset password
                                    </PillButton>
                                    <PillButton onClick={() => openUserAction(u, 'mfa')}>
                                      Reset MFA
                                    </PillButton>
                                    <PillButton onClick={() => openUserAction(u, 'email')}>
                                      Change email
                                    </PillButton>
                                  </>
                                ) : (
                                  <span className="text-xs text-[var(--color-muted)]">Managed externally</span>
                                )}
                                {u.isPrimaryContact ? (
                                  <span className="text-xs text-[var(--color-muted)]">Primary contact</span>
                                ) : (
                                  <PillButton
                                    onClick={() => setRemoveTarget({ userId: u.userId, displayName: u.displayName })}
                                    disabled={removingUserId === u.userId}
                                  >
                                    {removingUserId === u.userId ? 'Removing…' : 'Remove'}
                                  </PillButton>
                                )}
                              </div>
                            </div>

                            {userAction?.userId === u.userId ? (
                              <div className="mt-2 rounded-md border border-[rgb(var(--edge-rgb)/0.10)] glass-flat p-3">
                                {userAction.kind === 'email' ? (
                                  <>
                                    <p className="text-xs text-[var(--color-muted)] mb-2">
                                      Changes this member's <strong>contact email</strong> (where we send portal mail and
                                      invoices). It does <strong>not</strong> change their sign-in username.
                                    </p>
                                    <Input value={emailDraft} onChange={setEmailDraft} placeholder="name@example.com" type="email" />
                                    {sharedEmailMatches ? (
                                      <div className="mt-2 rounded-md bg-[var(--tint-red-bg-soft)] border border-[var(--tint-red-border)] px-3 py-2 text-xs text-[var(--tint-red-text)] space-y-2">
                                        <p className="font-medium">
                                          ⚠️ This contact email is already in use by {sharedEmailMatches.length} other
                                          account{sharedEmailMatches.length === 1 ? '' : 's'}:{' '}
                                          {sharedEmailMatches
                                            .map((s) => `${s.displayName} (${s.tenancyModel === 'member' ? 'member' : 'guest'})`)
                                            .join(', ')}
                                          .
                                        </p>
                                        <p>
                                          Sharing a contact email means password-reset links, security alerts,
                                          account-recovery messages, and account activity for all of these accounts land in
                                          the same inbox. Anyone with access to it can take over any of them. Only proceed
                                          for a genuinely shared, trusted mailbox (e.g. a family or a small team that already
                                          shares this address).
                                        </p>
                                        <label className="flex items-start gap-2">
                                          <input
                                            type="checkbox"
                                            className="mt-0.5"
                                            checked={sharedEmailAck}
                                            onChange={(e) => setSharedEmailAck(e.target.checked)}
                                          />
                                          <span>I accept this security and privacy risk. I want to set this account to a shared contact email.</span>
                                        </label>
                                      </div>
                                    ) : null}
                                    <div className="flex items-center gap-2 mt-2">
                                      <Button
                                        type="button"
                                        onClick={() => void runUserAction()}
                                        loading={actionBusy}
                                        disabled={!emailDraft.includes('@') || (!!sharedEmailMatches && !sharedEmailAck)}
                                      >
                                        {sharedEmailMatches ? 'Update anyway' : 'Update email'}
                                      </Button>
                                      <Button type="button" variant="ghost" onClick={closeUserAction} disabled={actionBusy}>
                                        Cancel
                                      </Button>
                                    </div>
                                  </>
                                ) : (
                                  <>
                                    <p className="text-sm mb-2">
                                      {userAction.kind === 'password'
                                        ? 'Email this member a temporary password? They will be prompted to set a new one at next sign-in.'
                                        : 'Remove this member\u2019s registered sign-in methods (Authenticator, phone, passkeys)? They will need to re-register. Their password is unchanged.'}
                                    </p>
                                    <div className="flex items-center gap-2">
                                      <Button type="button" onClick={() => void runUserAction()} loading={actionBusy}>
                                        {userAction.kind === 'password' ? 'Email temporary password' : 'Reset authentication methods'}
                                      </Button>
                                      <Button type="button" variant="ghost" onClick={closeUserAction} disabled={actionBusy}>
                                        Cancel
                                      </Button>
                                    </div>
                                  </>
                                )}
                                {actionError ? <p className="mt-2 text-sm text-[var(--tint-red-text)]">{actionError}</p> : null}
                              </div>
                            ) : null}

                            {actionResult?.userId === u.userId && userAction?.userId !== u.userId ? (
                              <p className="mt-1.5 text-xs text-[var(--tint-green-text)]">{actionResult.text}</p>
                            ) : null}
                          </li>
                        );
                      })}
                    </ul>
                  )
                ) : null}
              </div>

              <ClientServicesPanel clientId={editing.client.id} clientName={editing.client.displayName} businesses={businesses} onChanged={() => void load()} />

              {formError ? (
                <div className="rounded-md bg-[var(--tint-red-bg-soft)] border border-[var(--tint-red-border)] px-4 py-3 text-sm text-[var(--tint-red-text)]">
                  {formError}
                </div>
              ) : null}

              <div className="flex gap-3 pt-1">
                <Button
                  type="submit"
                  loading={submitting}
                  disabled={submitting || Object.keys(fieldErrors).length > 0}
                >
                  Save changes
                </Button>
                <Button type="button" variant="ghost" onClick={closeForm} disabled={submitting}>
                  Cancel
                </Button>
              </div>
            </form>
          </Card>
        </div>
      ) : null}

      {loading ? <p className="text-sm text-[var(--color-muted)]">Loading…</p> : null}
      {error ? <p className="text-sm text-[var(--tint-red-text)]">Error: {error}</p> : null}

      {data && data.clients.length === 0 ? (
        <Card>
          <p className="text-sm text-[var(--color-muted)]">
            No clients yet. <Link to="/admin/clients/invite" className="underline">Invite your first client</Link>.
          </p>
        </Card>
      ) : null}

      {data && data.clients.length > 0 ? (
        <div className="overflow-hidden rounded-lg border border-[rgb(var(--edge-rgb)/0.10)] glass-flat">
          <table className="w-full text-sm">
            <thead className="bg-[rgb(var(--edge-rgb)/0.05)] text-left">
              <tr>
                <th className="px-4 py-3 font-medium">Name</th>
                <th className="px-4 py-3 font-medium">Type</th>
                <th className="px-4 py-3 font-medium">Contact email</th>
                <th className="px-4 py-3 font-medium">Last login</th>
                <th className="px-4 py-3 font-medium text-center">Services</th>
                <th className="px-4 py-3 font-medium text-center">Open invoices</th>
                <th className="px-4 py-3 font-medium text-center">Status</th>
                <th className="px-4 py-3 font-medium text-center">Edit</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[rgb(var(--edge-rgb)/0.05)]">
              {data.clients
                .filter(({ client }) =>
                  internalFilter === 'all' ? true : internalFilter === 'internal' ? client.isInternal === true : client.isInternal !== true,
                )
                .map(({ client, primaryUser, signInUsername, activeServiceCount, openInvoiceCount, openInvoiceTotal, linkedAccountCount }) => (
                <tr key={client.id}>
                  <td className="px-4 py-3 font-medium">
                    <div className="flex items-center gap-2">
                      <ClientRowMedia client={client} primaryUser={primaryUser} bust={imgBust} />
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="truncate">{client.displayName}</span>
                          {client.isInternal ? (
                            <span
                              className="inline-flex shrink-0 items-center rounded-full bg-[var(--tint-slate-bg)] px-2 py-0.5 text-[11px] font-medium text-[var(--tint-slate-text)]"
                              title="This brand's reserved internal client: your own self-assigned services. Never billed; cost lives on the linked license expenses."
                            >
                              Internal
                            </span>
                          ) : null}
                          {client.clientType === 'business' && (linkedAccountCount ?? 0) > 0 ? (
                            <LinkedAccountBadge count={linkedAccountCount ?? 0} />
                          ) : null}
                        </div>
                        {client.clientType === 'business' && primaryUser?.displayName ? (
                          <span className="block text-xs font-normal text-[var(--color-muted)] truncate">
                            {primaryUser.displayName}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-[var(--color-muted)]">
                    <span className="block">{firstPlusMore(personBrands(primaryUser, businesses))}</span>
                    <span className="block">{capitalize(client.clientType)}</span>
                    <span className="block text-xs opacity-75">
                      {typeThirdLine(client, data.clients)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-[var(--color-muted)]">
                    <span className="block">{primaryUser?.email ?? '-'}</span>
                    {signInUsername ? (
                      <span className="block text-xs opacity-75">{signInUsername}</span>
                    ) : null}
                  </td>
                  <td className="px-4 py-3 text-[var(--color-muted)]">
                    {primaryUser?.lastLoginAt ? new Date(primaryUser.lastLoginAt).toLocaleString() : '-'}
                  </td>
                  <td className="px-4 py-3 text-[var(--color-muted)] text-center">{activeServiceCount}</td>
                  <td className="px-4 py-3 text-[var(--color-muted)] text-center">
                    {openInvoiceCount > 0
                      ? `${openInvoiceCount} ($${openInvoiceTotal.toFixed(2)})`
                      : '-'}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {client.status === 'active' || client.status === 'suspended' ? (
                      <ActiveToggle
                        active={client.status === 'active'}
                        busy={togglingId === client.id}
                        label={client.displayName}
                        onToggle={() => toggleClientActive(client)}
                      />
                    ) : (
                      <StatusPill status={client.status} />
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex justify-center">
                      <PillButton
                        size="lg"
                        onClick={() => openEdit(client, primaryUser, signInUsername)}
                        disabled={submitting || togglingId === client.id}
                      >
                        Edit
                      </PillButton>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      <PendingCreditsDialog
        pending={archivePending?.pending ?? null}
        trigger="archive"
        clientName={editing?.client.displayName ?? 'this client'}
        onClose={() => {
          // Closing cancels the archive: nothing was written and the status stays as it was.
          setArchivePending(null);
          setFormError('The archive was not applied: the credit still owed is unresolved (closing the dialog keeps it carrying).');
        }}
        onResolved={(res, refunds) => {
          const pendingPatch = archivePending?.patch;
          const clientId = editing?.client.id;
          setArchivePending(null);
          if (res) setSyncWarning(null);
          if (refunds.length > 0 && clientId) {
            // The paid-source branch lives on the Invoices page; the archive does not run (the admin
            // archives again after recording the refund, and the remaining credits re-raise).
            setFormError(`${res ? `${resolvedSummary(res)} ` : ''}Record the refund on the Invoices page, then archive the client again.`);
            navigate(refundDeepLink(clientId, refunds[0]));
            return;
          }
          if (pendingPatch) void applyClientPatch(pendingPatch);
        }}
      />

      <ConfirmDialog
        open={removeTarget !== null}
        title="Remove this member?"
        confirmLabel="Remove member"
        cancelLabel="Keep member"
        confirmVariant="danger"
        busy={removingUserId !== null}
        onConfirm={() => void doRemoveUser()}
        onCancel={() => setRemoveTarget(null)}
      >
        <p>
          Remove <strong>{removeTarget?.displayName}</strong> from this client? They will immediately
          lose access to it.
        </p>
      </ConfirmDialog>
    </div>
  );
}

// s33 linked-account model: a small count chip on a BUSINESS client's row showing how many consumer
// accounts are linked to it (its people — clients where linkedBusinessClientId = this client). Rendered
// only when the count is > 0. The count comes from ListClientsResponse.linkedAccountCount, which the
// backend already emits (absent for consumer clients, where the concept does not apply).
/** 08 Q71 (owner ruling s241): the editor's warning for an address region no loaded tax profile covers. The
 *  wording is the resolver's own outcome (02i §8 item 10 Q2 (A)); the "ask the operator" clause is where the
 *  tenant-to-operator request (08 Q74) will attach once it exists. */
function UncoveredRegionNote({ region }: { region: string }): JSX.Element {
  return (
    <p className="rounded-md bg-[var(--tint-warn-bg-soft)] border border-[var(--tint-warn-border)] px-3 py-2 text-xs text-[var(--tint-warn-text)]">
      No tax profile covers {region.trim()}: every invoice line to this client collects no tax, and the invoice
      says the client may owe use tax to their state. If {region.trim()} is right and you expect to collect
      there, ask the operator to add the state's profile; if it is a typo, fix it here.
    </p>
  );
}

function LinkedAccountBadge({ count }: { count: number }): JSX.Element {
  return (
    <span
      title={`${count} linked account${count === 1 ? '' : 's'}`}
      className="inline-flex shrink-0 items-center rounded-full bg-[var(--color-accent)]/15 px-2 py-0.5 text-[11px] font-medium text-[var(--ink-brand)]"
    >
      {count} linked
    </span>
  );
}

// List-row image (04 #3): a BUSINESS always shows two thumbnails — its company logo (square) and its
// primary-contact avatar (circle) — each a placeholder when that image is missing, regardless of how
// many users the business has. A CONSUMER shows just the person's avatar. AvatarImage renders an
// initial on a tinted disc when its src is null.
function ClientRowMedia({
  client,
  primaryUser,
  bust,
}: {
  client: Client;
  primaryUser: User | null;
  bust: number;
}): JSX.Element {
  const isBusiness = client.clientType === 'business';
  const logoSrc = client.logoBlobRef
    ? `/api/client-logo?clientId=${encodeURIComponent(client.id)}&v=${bust}`
    : null;
  const photoSrc = primaryUser?.photoBlobRef
    ? `/api/avatar?userId=${encodeURIComponent(primaryUser.id)}&v=${bust}`
    : null;
  const photoName = primaryUser?.displayName ?? client.displayName;
  return (
    <span className="flex items-center -space-x-1">
      {isBusiness ? (
        <AvatarImage src={logoSrc} name={client.displayName} size={28} shape="square" />
      ) : null}
      <AvatarImage src={photoSrc} name={photoName} size={28} shape="circle" />
    </span>
  );
}

function ReadOnlyBox({ value }: { value: string }): JSX.Element {
  return (
    <p className="rounded-md border border-[rgb(var(--edge-rgb)/0.10)] bg-[rgb(var(--edge-rgb)/0.03)] px-3 py-2 text-sm text-[var(--color-muted)]">
      {value}
    </p>
  );
}

// Same rounded-full pill as the Services "Status" badge — green for the active state, muted gray
// otherwise — so the Clients list reads identically. Label is the capitalized status.
function StatusPill({ status }: { status: ClientStatus }): JSX.Element {
  const isActive = status === 'active';
  const label = status.charAt(0).toUpperCase() + status.slice(1);
  return (
    <span
      className={[
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
        isActive ? 'bg-[var(--tint-green-bg)] text-[var(--tint-green-text)]' : 'bg-[rgb(var(--edge-rgb)/0.10)] text-[var(--color-muted)]',
      ].join(' ')}
    >
      {label}
    </span>
  );
}

function DobPicker({
  month,
  day,
  year,
  error,
  onChange,
}: {
  month: string;
  day: string;
  year: string;
  error?: boolean;
  onChange: (patch: { dobMonth?: string; dobDay?: string; dobYear?: string }) => void;
}): JSX.Element {
  const thisYear = new Date().getFullYear();
  const years = Array.from({ length: 120 }, (_, i) => String(thisYear - i));
  const maxDay = daysInMonth(year ? Number(year) : null, month ? Number(month) : 0);
  const days = Array.from({ length: maxDay }, (_, i) => String(i + 1));

  // Keep day valid if the month/year change shrinks the month (e.g. 31 → Feb).
  const clampDay = (nextMonth: string, nextYear: string): string => {
    if (!day) return day;
    const max = daysInMonth(nextYear ? Number(nextYear) : null, nextMonth ? Number(nextMonth) : 0);
    return Number(day) > max ? String(max) : day;
  };

  const selectCls = [
    'rounded-md border px-3 py-2 text-sm bg-[var(--surface-solid)]',
    error ? 'border-[var(--tint-red-border)]' : 'border-[rgb(var(--edge-rgb)/0.10)]',
  ].join(' ');

  return (
    <div className="grid grid-cols-3 gap-2">
      <select
        aria-label="Birth month"
        className={selectCls}
        value={month}
        onChange={(e) => onChange({ dobMonth: e.target.value, dobDay: clampDay(e.target.value, year) })}
      >
        <option value="">Month</option>
        {MONTHS.map(([v, label]) => (
          <option key={v} value={v}>{label}</option>
        ))}
      </select>
      <select
        aria-label="Birth day"
        className={selectCls}
        value={day}
        onChange={(e) => onChange({ dobDay: e.target.value })}
      >
        <option value="">Day</option>
        {days.map((d) => (
          <option key={d} value={d}>{d}</option>
        ))}
      </select>
      <select
        aria-label="Birth year"
        className={selectCls}
        value={year}
        onChange={(e) => onChange({ dobYear: e.target.value, dobDay: clampDay(month, e.target.value) })}
      >
        <option value="">Year</option>
        {years.map((y) => (
          <option key={y} value={y}>{y}</option>
        ))}
      </select>
    </div>
  );
}


function Input({
  value,
  onChange,
  placeholder,
  type = 'text',
  required,
  mono,
  error,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  required?: boolean;
  mono?: boolean;
  error?: boolean;
}): JSX.Element {
  return (
    <input
      type={type}
      required={required}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={[
        'w-full rounded-md border px-3 py-2 text-sm bg-[var(--surface-solid)]',
        error ? 'border-[var(--tint-red-border)]' : 'border-[rgb(var(--edge-rgb)/0.10)]',
        mono ? 'font-mono text-xs' : '',
      ].join(' ')}
    />
  );
}

function Select({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: ReadonlyArray<readonly [string, string]>;
}): JSX.Element {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-md border border-[rgb(var(--edge-rgb)/0.10)] px-3 py-2 text-sm bg-[var(--surface-solid)]"
    >
      {options.map(([v, label]) => (
        <option key={v} value={v}>
          {label}
        </option>
      ))}
    </select>
  );
}
